export const BANNER_IMAGE_RECOMMENDATION = Object.freeze({
  recommendedWidth: 1200,
  recommendedHeight: 630,
  minimumWidth: 600,
  minimumHeight: 315,
  maxFileSizeMB: 4,
  acceptedMimeTypes: ["image/jpeg", "image/png", "image/webp"],
});

const readFileAsDataUrl = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(new Error("Unable to read selected file."));
    reader.readAsDataURL(file);
  });

const readImageDimensions = (dataUrl) =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve({ width: img.width, height: img.height });
    img.onerror = () =>
      reject(new Error("Selected file is not a valid image."));
    img.src = dataUrl;
  });

export const prepareBannerImageUpload = async (file) => {
  if (!file) {
    throw new Error("Please choose an image file.");
  }

  if (!BANNER_IMAGE_RECOMMENDATION.acceptedMimeTypes.includes(file.type)) {
    throw new Error("Only PNG, JPEG, and WEBP files are supported.");
  }

  const maxSizeBytes = BANNER_IMAGE_RECOMMENDATION.maxFileSizeMB * 1024 * 1024;
  // Base64 encoding adds ~33% overhead; the encoded JSON payload will be roughly
  // (file.size * 4/3) bytes.  Reject early if that would exceed the server limit.
  const approxEncodedSize = file.size * (4 / 3);
  if (approxEncodedSize > maxSizeBytes) {
    throw new Error(
      `Image must be ${BANNER_IMAGE_RECOMMENDATION.maxFileSizeMB}MB or smaller (base64 encoding overhead included).`,
    );
  }

  const dataUrl = await readFileAsDataUrl(file);
  const { width, height } = await readImageDimensions(dataUrl);

  if (
    width < BANNER_IMAGE_RECOMMENDATION.minimumWidth ||
    height < BANNER_IMAGE_RECOMMENDATION.minimumHeight
  ) {
    throw new Error(
      `Image must be at least ${BANNER_IMAGE_RECOMMENDATION.minimumWidth}x${BANNER_IMAGE_RECOMMENDATION.minimumHeight}px.`,
    );
  }

  const recommendedRatio =
    BANNER_IMAGE_RECOMMENDATION.recommendedWidth /
    BANNER_IMAGE_RECOMMENDATION.recommendedHeight;
  const actualRatio = width / height;
  const ratioDelta =
    Math.abs(actualRatio - recommendedRatio) / recommendedRatio;

  const warnings = [];
  if (ratioDelta > 0.03) {
    warnings.push(
      `Best results use ${BANNER_IMAGE_RECOMMENDATION.recommendedWidth}x${BANNER_IMAGE_RECOMMENDATION.recommendedHeight}px (1.91:1).`,
    );
  }

  return {
    dataUrl,
    width,
    height,
    warnings,
    fileName: file.name,
  };
};

export const buildBannerPlaceholderUrl = (title = "PurelyGive") =>
  `https://placehold.co/${BANNER_IMAGE_RECOMMENDATION.recommendedWidth}x${BANNER_IMAGE_RECOMMENDATION.recommendedHeight}/ffa726/fff?text=${encodeURIComponent(
    title || "PurelyGive",
  )}&font=Montserrat&bold=true&fontsize=56&rounded=true`;
